export interface StoredAccount {
  label: string;
  email: string;
  region: string;
  userPoolId: string;
  clientId: string;
  cognitoHostedDomain: string;
}

export const ACCOUNTS_KEY = 'aktify_accounts';
export const ADDING_ACCOUNT_KEY = 'aktify_adding_account';

const COGNITO_PREFIX = 'CognitoIdentityServiceProvider.';

// localStorage keys holding the active Amplify/Cognito config — the "active account" selector.
export const AMPLIFY_KEYS = {
  region: 'amplifyRegion',
  userPoolId: 'amplifyUserPoolId',
  clientId: 'amplifyClientId',
  cognitoHostedDomain: 'amplifyCognitoHostedDomain',
} as const;

export const getAccounts = (): StoredAccount[] => {
  try {
    const raw = localStorage.getItem(ACCOUNTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as StoredAccount[]) : [];
  } catch {
    return [];
  }
};

const saveAccounts = (accounts: StoredAccount[]): void => {
  localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts));
};

export const upsertAccount = (account: StoredAccount): void => {
  const accounts = getAccounts();
  const idx = accounts.findIndex((a) => a.clientId === account.clientId);
  if (idx >= 0) {
    accounts[idx] = account;
  } else {
    accounts.push(account);
  }
  saveAccounts(accounts);
};

export const removeAccount = (clientId: string): void => {
  saveAccounts(getAccounts().filter((a) => a.clientId !== clientId));
};

export const getActiveClientId = (): string | null => localStorage.getItem(AMPLIFY_KEYS.clientId);

export const clearAllAccounts = (): void => localStorage.removeItem(ACCOUNTS_KEY);

const removeKeysWithPrefix = (prefix: string): void => {
  const toRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix)) toRemove.push(key);
  }
  toRemove.forEach((key) => localStorage.removeItem(key));
};

export const clearPoolTokens = (clientId: string): void =>
  removeKeysWithPrefix(`${COGNITO_PREFIX}${clientId}.`);

export const clearAllPoolTokens = (): void => removeKeysWithPrefix(COGNITO_PREFIX);

export const activateAccount = (account: StoredAccount): void => {
  localStorage.setItem(AMPLIFY_KEYS.region, account.region);
  localStorage.setItem(AMPLIFY_KEYS.userPoolId, account.userPoolId);
  localStorage.setItem(AMPLIFY_KEYS.clientId, account.clientId);
  localStorage.setItem(AMPLIFY_KEYS.cognitoHostedDomain, account.cognitoHostedDomain);
};

export const getActiveAccountConfig = (email: string): Omit<StoredAccount, 'label'> | null => {
  const clientId = localStorage.getItem(AMPLIFY_KEYS.clientId);
  if (!clientId) return null;
  return {
    email,
    region: localStorage.getItem(AMPLIFY_KEYS.region) || '',
    userPoolId: localStorage.getItem(AMPLIFY_KEYS.userPoolId) || '',
    clientId,
    cognitoHostedDomain: localStorage.getItem(AMPLIFY_KEYS.cognitoHostedDomain) || '',
  };
};

export const defaultLabelForEmail = (email: string): string => {
  const local = email.split('@')[0];
  return local || 'Account';
};

// Single source of truth for how account labels are compared (case-insensitive, trimmed).
export const normalizeLabel = (label: string): string => label.trim().toLowerCase();

export const isLabelInUse = (label: string, existing: string[]): boolean =>
  existing.some((l) => normalizeLabel(l) === normalizeLabel(label));

// The labels of every account other than `exceptClientId` — used to reject/uniquify duplicates.
export const otherLabels = (exceptClientId?: string): string[] =>
  getAccounts()
    .filter((a) => a.clientId !== exceptClientId)
    .map((a) => a.label);

// Returns `base` if free, otherwise appends the lowest number that isn't taken ("jane" → "jane 2").
export const uniqueLabel = (base: string, taken: string[]): string => {
  if (!isLabelInUse(base, taken)) return base;
  let n = 2;
  while (isLabelInUse(`${base} ${n}`, taken)) n++;
  return `${base} ${n}`;
};

// The account we'd fall back to after removing `excludeClientId` (first other account, if any).
export const getFallbackAccount = (excludeClientId: string): StoredAccount | undefined =>
  getAccounts().find((a) => a.clientId !== excludeClientId);

// clientId to re-activate if the user cancels an in-progress add — only meaningful while adding.
export const RETURN_CLIENT_KEY = 'aktify_add_return_client';

export const isAddingAccount = (): boolean => sessionStorage.getItem(ADDING_ACCOUNT_KEY) === '1';

export const setAddingAccount = (value: boolean): void => {
  if (value) {
    sessionStorage.setItem(ADDING_ACCOUNT_KEY, '1');
  } else {
    // The return-client pointer lives and dies with the adding flag — clear them together
    // so there is a single owner of add-flow teardown.
    sessionStorage.removeItem(ADDING_ACCOUNT_KEY);
    sessionStorage.removeItem(RETURN_CLIENT_KEY);
  }
};

export const getAddReturnClient = (): string | null => sessionStorage.getItem(RETURN_CLIENT_KEY);

export const setAddReturnClient = (clientId: string): void =>
  sessionStorage.setItem(RETURN_CLIENT_KEY, clientId);

// True when the active Amplify pool isn't in the registry yet but other accounts exist —
// i.e. we just returned from adding a new account and it still needs a label.
export const hasUnregisteredActiveAccount = (): boolean => {
  const active = getActiveClientId();
  const accounts = getAccounts();
  return !!active && accounts.length > 0 && !accounts.some((a) => a.clientId === active);
};
